import { useCallback, useEffect, useRef, useState } from "react";

const EDGE = 90;
const LONG_PRESS_MS = 350;

const findScroller = (el) => {
  let n = el?.parentElement;
  while (n && n !== document.body) {
    const o = getComputedStyle(n).overflowY;
    if ((o === "auto" || o === "scroll") && n.scrollHeight > n.clientHeight) return n;
    n = n.parentElement;
  }
  return null;
};
const getScroll = (sc) => (sc ? sc.scrollTop : window.scrollY);

// Reordenar arrastrando: mango inmediato o mantener presionado el bloque (touch).
export const useDragSort = (containerRef, onReorder) => {
  const st = useRef(null);
  const press = useRef(null);
  const swallowClick = useRef(false);
  const reorderRef = useRef(onReorder);
  reorderRef.current = onReorder;
  const [dragIndex, setDragIndex] = useState(null);

  const update = useCallback((clientY) => {
    const s = st.current;
    if (!s) return;
    s.y = clientY;
    const dy = clientY - s.startY + (getScroll(s.sc) - s.startScroll);
    const { rects, index, els, slot } = s;
    els[index].style.transform = `translateY(${dy}px) scale(1.02)`;
    const center = clientY + getScroll(s.sc);
    let to = index;
    for (let i = index + 1; i < rects.length; i++) if (center > rects[i].top + rects[i].h / 2) to = i;
    for (let i = index - 1; i >= 0; i--) if (center < rects[i].top + rects[i].h / 2) to = i;
    s.to = to;
    els.forEach((e, i) => {
      if (i === index) return;
      let t = 0;
      if (to > index && i > index && i <= to) t = -slot;
      if (to < index && i >= to && i < index) t = slot;
      e.style.transform = t ? `translateY(${t}px)` : "";
    });
  }, []);

  const loop = useCallback(() => {
    const s = st.current;
    if (!s) return;
    const h = window.innerHeight;
    let v = 0;
    if (s.y < EDGE) v = -Math.ceil((EDGE - s.y) / 4);
    else if (s.y > h - EDGE) v = Math.ceil((s.y - (h - EDGE)) / 4);
    if (v) {
      if (s.sc) s.sc.scrollTop += v;
      else window.scrollBy(0, v);
      update(s.y);
    }
    s.raf = requestAnimationFrame(loop);
  }, [update]);

  const begin = useCallback((index, clientY) => {
    const root = containerRef.current;
    if (!root || st.current) return;
    const els = [...root.querySelectorAll("[data-sort-item]")];
    if (!els[index]) return;
    const sc = findScroller(root);
    const s0 = getScroll(sc);
    const rects = els.map((e) => {
      const r = e.getBoundingClientRect();
      return { top: r.top + s0, h: r.height };
    });
    const next = rects[index + 1] || null;
    const prev = rects[index - 1] || null;
    const gap = next ? next.top - rects[index].top - rects[index].h : prev ? rects[index].top - prev.top - prev.h : 0;
    st.current = { index, to: index, startY: clientY, startScroll: s0, rects, els, sc, y: clientY, slot: rects[index].h + gap, raf: 0 };
    els.forEach((e, i) => {
      e.style.transition = i === index ? "box-shadow 150ms ease" : "transform 160ms ease";
      e.style.willChange = "transform";
    });
    els[index].classList.add("ds-dragging");
    document.body.classList.add("ds-active");
    if (navigator.vibrate) navigator.vibrate(12);
    setDragIndex(index);
    st.current.raf = requestAnimationFrame(loop);
  }, [containerRef, loop]);

  const finish = useCallback(() => {
    const s = st.current;
    if (!s) return;
    cancelAnimationFrame(s.raf);
    st.current = null;
    s.els.forEach((e) => {
      e.style.transition = "none";
      e.style.transform = "";
      e.style.willChange = "";
      e.classList.remove("ds-dragging");
    });
    document.body.classList.remove("ds-active");
    setDragIndex(null);
    if (s.to !== s.index) reorderRef.current(s.index, s.to);
    requestAnimationFrame(() => s.els.forEach((e) => (e.style.transition = "")));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const p = press.current;
      const t = e.touches ? e.touches[0] : e;
      if (!t) return;
      if (st.current) {
        if (e.cancelable) e.preventDefault();
        update(t.clientY);
      } else if (p && Math.hypot(t.clientX - p.x, t.clientY - p.y) > 10) {
        clearTimeout(p.timer);
        press.current = null;
      }
    };
    const onEnd = () => {
      if (press.current) clearTimeout(press.current.timer);
      press.current = null;
      if (st.current) {
        swallowClick.current = true;
        setTimeout(() => (swallowClick.current = false), 350);
        finish();
      }
    };
    const onClick = (e) => {
      if (swallowClick.current) {
        e.preventDefault();
        e.stopPropagation();
        swallowClick.current = false;
      }
    };
    const onCtx = (e) => st.current || press.current ? e.preventDefault() : null;
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("pointermove", onMove);
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("click", onClick, true);
    window.addEventListener("contextmenu", onCtx);
    return () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("contextmenu", onCtx);
    };
  }, [update, finish]);

  // Mango: arrastre inmediato (dedo o mouse).
  const handleProps = (index) => ({
    "data-drag-handle": "",
    style: { touchAction: "none", cursor: "grab" },
    onPointerDown: (e) => {
      e.preventDefault();
      e.stopPropagation();
      begin(index, e.clientY);
    },
  });

  // Bloque: mantener presionado para arrastrar (solo touch).
  const itemProps = (index) => ({
    "data-sort-item": "",
    onDragStart: (e) => e.preventDefault(),
    onTouchStart: (e) => {
      if (e.target.closest("[data-drag-handle]") || e.touches.length > 1) return;
      const t = e.touches[0];
      const p = { x: t.clientX, y: t.clientY };
      p.timer = setTimeout(() => {
        if (press.current === p) begin(index, p.y);
      }, LONG_PRESS_MS);
      press.current = p;
    },
  });

  return { dragIndex, handleProps, itemProps };
};
